import { navigateTo } from '#imports'
import { type Ref, onMounted } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'

import { useCreateLinkFromPicker } from '~/scopes/product-core/components/presentationals/CustomizationFunnel/composables/useCreateLinkFromPicker/useCreateLinkFromPicker'
import type { Step } from '~/scopes/product-core/components/presentationals/CustomizationFunnel/utils/types'

import { BATTERY_OFFER_TYPE } from '../../Product-JP.constants'

type BatteryItem = GetPickersResponse['pickerGroups'][number]['items'][number]
type VisibleBatteryItem = BatteryItem & {
  productId: string
  slug: string
  price: NonNullable<BatteryItem['price']>
}

/**
 * On the Japan PP, when the battery picker A/B test is in its control variant,
 * the 90-99% battery option is hidden from the funnel UI but the backend's
 * `selectedOffer` may still point to a 90-99% offer (it is the cheapest across
 * all battery types under `cheapest_by_grade`). The price and add-to-cart then
 * correspond to an option the user cannot see or change.
 *
 * This composable detects that state and navigates to the cheapest visible
 * battery variant (standard or new battery) using the same link the funnel
 * would build on a user click — this sets `variantClicked=true` in the query,
 * which turns off `cheapest_by_grade` on the pickers refetch, so the backend
 * returns a `selectedOffer` for the visible variant instead.
 *
 * JP-only. Delete this file and its wiring in Product-JP.vue once the
 * `ppJapanBatteryPickerUpdated` experiment concludes.
 */
export const useFixHiddenBatteryPicker = ({
  pickersResponse,
  selectedOffer,
  useBatteryPickerUpdated,
}: {
  pickersResponse: Ref<GetPickersResponse | null | undefined>
  selectedOffer: Ref<GetPickersResponse['selectedOffer'] | null | undefined>
  useBatteryPickerUpdated: Ref<boolean>
}) => {
  const { createLink } = useCreateLinkFromPicker()

  onMounted(async () => {
    if (useBatteryPickerUpdated.value) {
      return
    }

    if (
      selectedOffer.value?.offerType.value !== BATTERY_OFFER_TYPE.NINETY_NINE
    ) {
      return
    }

    const batteryGroup = pickersResponse.value?.pickerGroups.find(
      (group) => group.id === 'battery',
    )

    if (!batteryGroup) {
      return
    }

    const candidates = batteryGroup.items.filter(
      (item): item is VisibleBatteryItem => {
        const type = item.parameters?.specialOfferType?.value

        return (
          item.available &&
          !!item.productId &&
          !!item.slug &&
          item.price?.amount !== undefined &&
          (type === BATTERY_OFFER_TYPE.STANDARD ||
            type === BATTERY_OFFER_TYPE.NEW)
        )
      },
    )

    const cheapest = candidates.reduce<VisibleBatteryItem | null>(
      (best, item) =>
        !best || parseFloat(item.price.amount) < parseFloat(best.price.amount)
          ? item
          : best,
      null,
    )

    if (!cheapest) {
      return
    }

    const link = createLink(cheapest, { id: 'battery' } as Step)

    if (!link) {
      return
    }

    await navigateTo(link, { replace: true })
  })
}
